/* eslint-disable @typescript-eslint/naming-convention */

import React, { createContext, useContext } from 'react';
import type {
  CategoryPageSubheaderEntry,
  CustomTagFilterConfig,
  Events,
  ListPageEntry,
  ModuleRegistryEntry,
  RemoteModuleRegistry,
  StoreFrontCarouselTitleProps,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import type { CategoryPageBelowListIterable } from 'src/dispensary/category/category-page-below-list';

type FallbackProps = {
  fallback?: React.FC<any>;
  pending?: React.FC<any>;
  renderRemote?: (component: React.ReactElement) => React.ReactElement;
};
type DefaultComponentSig = (options?: FallbackProps) => React.FC<any>;
type CategoryIterable = { category: ListPageEntry['category']; components: React.ElementType[] };

export type RouteablePage = {
  path: string;
  component: DefaultComponentSig;
};

// The `theme` prop forwarded verbatim to remote (module-federation) components — see
// remote-theme.tsx's `wrapComponentFactory` and README.md's "Accept and use the data and theme
// props". Today it's always the styled-components/MUI theme object built by `getStyledTheme`
// (theme/builders/styled.js, untyped JS) — never a string — but nothing in this subsystem reads
// any of its properties, so it's typed as an opaque plain object rather than that builder's
// specific (currently unnamed) shape.
export type RemoteTheme = Record<string, unknown> | null;

// TEMPORARY: Local shims for smart tags until @dutchiesdk/ecommerce-extensions-sdk exports them (ECOMM-12637)
export type CustomSmartTagFilterConfig = {
  category: string;
  name: string;
  title: string;
  match: string[];
};

// TODO: ECOMM-13280 - Remove ExtendedRemoteModuleRegistry once @dutchiesdk/ecommerce-extensions-sdk exports SpecialsCard
// TODO: ECOMM-12637 - Remove enableCustomSmartTagFilters once @dutchiesdk/ecommerce-extensions-sdk exports it
export type ExtendedRemoteModuleRegistry = RemoteModuleRegistry & {
  SpecialsCard?: ModuleRegistryEntry;
  enableCustomSmartTagFilters?: CustomSmartTagFilterConfig[];
};

export type RemoteModuleRegistryWithSmartTagFilters = ExtendedRemoteModuleRegistry;

export type ThemeConfig = {
  customTagFilters: CustomTagFilterConfig[];
  customSmartTagFilters: CustomSmartTagFilterConfig[];
};

export type CommerceComponents = {
  components: {
    [K in keyof Omit<
      ExtendedRemoteModuleRegistry,
      | 'CategoryPageBelowList'
      | 'CategoryPageInterstitials'
      | 'CategoryPageSlots'
      | 'CategoryPageSubheaders'
      | 'enableCustomSmartTagFilters'
      | 'enableCustomTagFilters'
      | 'events'
      | 'getStoreFrontMetaFields'
      | 'RouteablePages'
      | 'StoreFrontCarouselInterstitials'
      | 'StoreFrontCarouselTitles'
    >]-?: DefaultComponentSig;
  };
  findCarouselTitle: (o: {
    carouselId: string;
    fallback?: React.FC<StoreFrontCarouselTitleProps>;
  }) => React.FC<StoreFrontCarouselTitleProps>;
  findPage: (o: { fallback?: React.FC; path?: string }) => React.FC;
  iterables: {
    StoreFrontCarouselInterstitials: () => React.ElementType[];
    CategoryPageInterstitials: () => CategoryIterable[];
    CategoryPageSlots: () => CategoryIterable[];
    CategoryPageBelowList: () => CategoryPageBelowListIterable[];
    /**
     * Not a component -- raw, unresolved SDK-authored entries for the
     * optional category page sub-header (ECOMM-13289). Consumers should
     * resolve/validate the matching entry via
     * `findMatchingCategorySubheaderText` rather than reading `text` off
     * these directly.
     */
    CategoryPageSubheaders: () => CategoryPageSubheaderEntry[];
  };
  pages: ({ fallback }: FallbackProps) => RouteablePage[];
  events: Events | undefined;
  isRemoteThemeResolved: boolean;
  themeConfig: ThemeConfig;
};

const EmptyComponent: React.FC<any> = () => <></>;

export const defaultCommerceComponents: CommerceComponents = {
  components: {
    StoreFrontHeader: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontNavigation: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontFooter: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    GlobalStyles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoyaltyWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoginWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontCategoryTiles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontSpecialsCategoryTiles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ListPageHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsPrimary: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCard: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCardListItem: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    FilterSidePanel: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    SpecialsPageHeader: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    SpecialsCard: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
  },
  findCarouselTitle: ({ fallback = EmptyComponent }) => fallback,
  findPage: ({ fallback = () => <></> }) => fallback,
  iterables: {
    StoreFrontCarouselInterstitials: () => [],
    CategoryPageInterstitials: () => [],
    CategoryPageSlots: () => [],
    CategoryPageBelowList: () => [],
    CategoryPageSubheaders: () => [],
  },

  pages: (_props: FallbackProps = { fallback: () => <></> }) => [],
  events: {},
  isRemoteThemeResolved: false,
  themeConfig: { customTagFilters: [], customSmartTagFilters: [] },
};

export const CommerceComponentsContext = createContext<CommerceComponents>(defaultCommerceComponents);

export const useCommerceComponents = (): CommerceComponents => useContext(CommerceComponentsContext);
